import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { InvestmentsAPI } from '../services/investments.ts';
import { formatCurrency, getCurrencyLocale, safeCurrencyCode } from '../utils/currency.ts';
import { investmentCategoryLabel } from '../utils/labels.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';
import PortfolioPieChart from '../components/investments/PortfolioPieChart.tsx';
import PortfolioHistoryChart from '../components/investments/PortfolioHistoryChart.tsx';

const formatMoney = (value: number, currency = 'BRL') => {
  const code = safeCurrencyCode(currency);
  return formatCurrency(value, code, getCurrencyLocale(code));
};

function asInvestmentList(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as any).data))
    return (data as any).data;
  return [];
}

const Investments: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, isFetching, error, isError } = useQuery(
    'investments',
    () => InvestmentsAPI.list(),
    { staleTime: 15_000, retry: 1 }
  );

  const { data: portfolio, isFetching: portfolioFetching } = useQuery(
    ['investments-portfolio', statusFilter, categoryFilter, searchTerm],
    () =>
      InvestmentsAPI.portfolioCharts({
        status: statusFilter,
        category: categoryFilter,
        search: searchTerm,
      }),
    // No keepPreviousData: history must not keep the previous filter's series
    { staleTime: 15_000, retry: 1 }
  );

  const filtered = useMemo(() => {
    return asInvestmentList(data).filter((item: any) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        String(item.description || '')
          .toLowerCase()
          .includes(q) ||
        String(item.ticker || '')
          .toLowerCase()
          .includes(q);
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [data, searchTerm, statusFilter, categoryFilter]);

  const kpis = portfolio?.kpis;
  const emptyPortfolio = !portfolio || portfolio.empty;
  const history = portfolio?.history || [];
  const historyChartKey = [
    statusFilter,
    categoryFilter,
    searchTerm,
    history.length,
    history[0]?.portfolio,
    history[history.length - 1]?.portfolio,
    portfolio?.selic?.source,
  ].join('|');

  if (isLoading && !data && !isError) {
    return (
      <div
        className="flex items-center justify-center h-64"
        aria-busy="true"
        data-loading="investments"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (isError || error) return <div className="field-error">Erro ao carregar investimentos.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Investimentos"
        title="Investimentos"
        subtitle="Carteira · gráficos"
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select
              className="select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Ativo</option>
              <option value="sold">Encerrado</option>
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">Categoria</span>
            <select
              className="select"
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="fixed_income">Renda Fixa</option>
              <option value="stocks">Ações</option>
              <option value="funds">Fundos</option>
              <option value="crypto">Cripto</option>
              <option value="offshore">Offshore</option>
              <option value="other">Outros</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Nome ou ticker"
            />
          </label>
        </div>
        {(hasPermission('investments.create') || hasPermission('assets.create')) && (
          <div className="toolbar-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/investments/importar')}
            >
              Importar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/investments/novo')}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo
            </button>
          </div>
        )}
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Posições</div>
          <div className="kpi-value">{kpis?.positions ?? filtered.length}</div>
          <div className="kpi-hint">
            {kpis?.positionsHint || '—'}
            {portfolioFetching ? ' · atualizando…' : ''}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Patrimônio investido</div>
          <div className="kpi-value">{formatMoney(kpis?.patrimonio ?? portfolio?.total ?? 0)}</div>
          <div className="kpi-hint">
            {statusFilter !== 'all' || categoryFilter !== 'all' || searchTerm
              ? 'filtro atual · valor de mercado'
              : 'valor de mercado'}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Rendimento (mês)</div>
          <div
            className="kpi-value"
            style={{
              color: (kpis?.rendimentoMes ?? 0) >= 0 ? undefined : '#dc2626',
            }}
          >
            {formatMoney(kpis?.rendimentoMes ?? 0)}
          </div>
          <div className="kpi-hint" style={{ color: '#16a34a' }}>
            {(kpis?.rendimentoMesPct ?? 0) >= 0 ? '+' : ''}
            {(kpis?.rendimentoMesPct ?? 0).toLocaleString('pt-BR', {
              maximumFractionDigits: 1,
            })}
            %
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Vencendo (90d)</div>
          <div className="kpi-value">{kpis?.vencendo90d ?? 0}</div>
          <div className="kpi-hint">RF a rolar</div>
        </div>
      </div>

      <div className="charts-row">
        <div className="card chart-card">
          <div className="card-header">
            <div>
              <div className="card-title">Posição por categoria</div>
              <span className="muted">Gráfico de pizza</span>
            </div>
          </div>
          <PortfolioPieChart
            slices={portfolio?.byCategory || []}
            total={portfolio?.total || 0}
            empty={emptyPortfolio}
          />
        </div>
        <div className="card chart-card">
          <div className="card-header">
            <div>
              <div className="card-title">Histórico da carteira</div>
              <span className="muted">
                vs SELIC · eixo Y: índice (base 100) · eixo X: meses
                {portfolio?.selic?.currentRateLabel
                  ? ` · ${portfolio.selic.currentRateLabel}`
                  : ''}
                {portfolio?.selic?.sourceLabel
                  ? ` · hist. ${portfolio.selic.sourceLabel}`
                  : ''}
                {portfolio?.appliedFilters &&
                (portfolio.appliedFilters.status !== 'all' ||
                  portfolio.appliedFilters.category !== 'all' ||
                  portfolio.appliedFilters.search)
                  ? ` · filtro: ${portfolio.appliedFilters.positions} posições`
                  : ''}
              </span>
            </div>
          </div>
          <PortfolioHistoryChart
            key={historyChartKey}
            points={history}
            selicLabel={portfolio?.selic?.label || 'SELIC (índice)'}
            empty={emptyPortfolio || history.length === 0}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Investimentos</div>
          <span className="muted">
            {filtered.length} registros{isFetching ? ' · atualizando…' : ''}
          </span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum investimento encontrado. Use <strong>Importar</strong> ou <strong>Novo</strong>{' '}
            para começar.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Investimento</th>
                  <th className="hide-m">Tipo</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>
                    Valor
                  </th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item: any) => {
                  const currency = safeCurrencyCode(item.currency);
                  return (
                    <tr key={item.id}>
                      <td>
                        <StackCell
                          title={item.description}
                          meta={`${investmentCategoryLabel(item.category)}${
                            item.ticker ? ` · ${item.ticker}` : ''
                          }`}
                          stackExtra={<>{formatMoney(Number(item.currentValue || 0), currency)}</>}
                        />
                      </td>
                      <td className="hide-m">{investmentCategoryLabel(item.category)}</td>
                      <td className="hide-m">
                        <StatusChip
                          label={formatStatus(item.status)}
                          tone={item.status === 'active' ? 'green' : 'slate'}
                        />
                      </td>
                      <td className="amount hide-m" style={{ textAlign: 'right' }}>
                        {formatMoney(Number(item.currentValue || 0), currency)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="row-actions">
                          <OpenAction onClick={() => navigate(`/investments/${item.id}`)} />
                          {hasPermission('investments.delete') && (
                            <button
                              type="button"
                              className="link-btn action-keep action-danger"
                              onClick={() => {
                                setDeleteId(item.id);
                                setShowDeleteModal(true);
                              }}
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteId(null);
        }}
        title="Excluir Investimento"
        message="Tem certeza que deseja excluir este investimento? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await InvestmentsAPI.remove(String(deleteId));
            queryClient.invalidateQueries(['investments']);
            queryClient.invalidateQueries(['investments-portfolio']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Investments;
